// Magic autofill — detect unfilled (near-white) connected regions on a canvas
// and sample replacement colors from the reference colored image. Pure
// functions; the engine wires them together in ColoringEngine.magicAutofill.
//
// Designed to be called after the user has manually colored part of an artwork
// and presses the "魔法補色 ✨" button. The engine snapshots state ONCE so a
// single undo reverts the entire batch (see engine.ts).

import { floodFill } from './floodfill.ts'
import type { RGB } from './types.ts'

/** Per-channel "this pixel counts as unfilled white" cutoff (strict >). */
const NEAR_WHITE_PER_CHANNEL = 245
/** Per-channel "this pixel is a line" cutoff (strict <). Mirrors floodfill.ts. */
const LINE_PER_CHANNEL = 60
/** Per-channel "sample is too white to be a real colored region" cutoff. */
const SAMPLE_TOO_WHITE = 240
/** Per-channel "sample is too black, probably line bleed in JPG" cutoff. */
const SAMPLE_TOO_BLACK = 30
/**
 * HSV-style brightness multiplier applied to averaged samples. 1.0 = no
 * change. Per-pixel cap so the brightest channel never overflows 255.
 * Hue and saturation stay put; only the value channel rises.
 *
 * History: white-mix 0.2 (round-1 dogfood read as "彩度降低, 老照片感") →
 * HSV scale 1.25 (read as 仍偏深) → 1.4 (太亮) → 1.35 (主公 verdict).
 */
const BRIGHTNESS_MULTIPLIER = 1.35 // 主公-tuned (1.25→1.4 too bright→settled 1.35)
/**
 * Saturation boost applied after the brightness pass. Pulls each channel
 * away from the pixel's luminance by this factor, so dominant channels
 * get more dominant and weak ones recede — colors read more vibrant.
 * 1.0 = no change. 主公 note: "顏色比較偏紅棕, 可不可以彩度豐富一點" —
 * the reference colored.jpg uses a Middle-Eastern palette that lands
 * earthy out of the box. A small saturation lift makes kids' output
 * read as cartoon-vibrant without changing hues.
 */
const SATURATION_MULTIPLIER = 1.3

export interface Region {
  /** Centroid (mean of all pixels in region). Floats — caller floors when sampling. */
  centerX: number
  centerY: number
  /**
   * A pixel known to be INSIDE the region (the first white+unvisited pixel
   * the scan found). Floods and samples should start here — the centroid
   * can fall on a line or outside the region for concave shapes (e.g.
   * Joseph's coat sleeve curving around the body), which causes floodFill
   * to bail with `on-line` and the region never gets painted.
   */
  seedX: number
  seedY: number
  pixelCount: number
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
}

export interface DetectArgs {
  /** RGBA, length 4 * width * height. Read-only — function does not mutate. */
  data: Uint8ClampedArray
  width: number
  height: number
}

/**
 * Find all connected components of near-white (unfilled) pixels, excluding
 * line pixels. Connectivity = 4-neighbour. Returns one Region per component.
 *
 * Visited bitmap is one byte per pixel so a 1408×786 canvas allocs ~1.1 MB
 * once per call. The scanline / stack pattern matches floodfill.ts so behavior
 * stays consistent with the bucket tool's notion of "region".
 */
export function detectUnfilledRegions({ data, width, height }: DetectArgs): Region[] {
  const visited = new Uint8Array(width * height)
  const regions: Region[] = []

  const isUnfilledWhite = (px: number, py: number): boolean => {
    if (px < 0 || px >= width || py < 0 || py >= height) return false
    if (visited[py * width + px]) return false
    const i = (py * width + px) * 4
    const r = data[i]!
    const g = data[i + 1]!
    const b = data[i + 2]!
    // Exclude line pixels (binarized black)
    if (r < LINE_PER_CHANNEL && g < LINE_PER_CHANNEL && b < LINE_PER_CHANNEL) return false
    // Include only near-white (unfilled) pixels
    return r > NEAR_WHITE_PER_CHANNEL && g > NEAR_WHITE_PER_CHANNEL && b > NEAR_WHITE_PER_CHANNEL
  }

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (visited[y * width + x]) continue
      const i0 = (y * width + x) * 4
      // Fast reject: line or already-filled pixel — mark visited & move on
      const r0 = data[i0]!
      const g0 = data[i0 + 1]!
      const b0 = data[i0 + 2]!
      const isLine = r0 < LINE_PER_CHANNEL && g0 < LINE_PER_CHANNEL && b0 < LINE_PER_CHANNEL
      const isWhite = r0 > NEAR_WHITE_PER_CHANNEL && g0 > NEAR_WHITE_PER_CHANNEL && b0 > NEAR_WHITE_PER_CHANNEL
      if (isLine || !isWhite) {
        visited[y * width + x] = 1
        continue
      }

      // Scanline flood-fill component starting at (x, y)
      const stack: number[] = [y * width + x]
      let pixelCount = 0
      let sumX = 0
      let sumY = 0
      let minX = x
      let minY = y
      let maxX = x
      let maxY = y

      while (stack.length > 0) {
        const seed = stack.pop()!
        const sx = seed % width
        const sy = Math.floor(seed / width)
        if (!isUnfilledWhite(sx, sy)) continue

        // Find left edge of the run on this row
        let lx = sx
        while (lx > 0 && isUnfilledWhite(lx - 1, sy)) lx--

        let cx = lx
        let spanAbove = false
        let spanBelow = false
        while (cx < width && isUnfilledWhite(cx, sy)) {
          visited[sy * width + cx] = 1
          pixelCount++
          sumX += cx
          sumY += sy
          if (cx < minX) minX = cx
          if (cx > maxX) maxX = cx
          if (sy < minY) minY = sy
          if (sy > maxY) maxY = sy

          if (sy > 0) {
            const above = isUnfilledWhite(cx, sy - 1)
            if (above && !spanAbove) {
              stack.push((sy - 1) * width + cx)
              spanAbove = true
            } else if (!above) {
              spanAbove = false
            }
          }
          if (sy < height - 1) {
            const below = isUnfilledWhite(cx, sy + 1)
            if (below && !spanBelow) {
              stack.push((sy + 1) * width + cx)
              spanBelow = true
            } else if (!below) {
              spanBelow = false
            }
          }
          cx++
        }
      }

      if (pixelCount > 0) {
        regions.push({
          centerX: sumX / pixelCount,
          centerY: sumY / pixelCount,
          // The outer scan only enters this branch when (x, y) is a white,
          // non-line, unvisited pixel — so it's guaranteed to be inside the
          // region we just flood-marked. Stash it as the seed for downstream
          // floodFill / sampling.
          seedX: x,
          seedY: y,
          pixelCount,
          bounds: { minX, minY, maxX, maxY },
        })
      }
    }
  }

  return regions
}

export interface SampleArgs {
  /** RGBA of the colored reference image. Same dimensions as the canvas. */
  coloredData: Uint8ClampedArray
  /** Width of the colored reference image (used to compute pixel index). */
  width: number
  region: Region
}

/**
 * Sample a representative color from the colored reference image for the
 * given region. Tries the centroid pixel first; if that's too white or too
 * black (JPEG artifact, line bleed), averages 8 fallback samples spread
 * across the region's bounding box. Returns null if all samples are invalid.
 */
export function sampleRegionColor({ coloredData, width, region }: SampleArgs): RGB | null {
  // Gather ALL candidate sample points (seed, centroid, 3×3 grid in bounds)
  // and average the valid ones. Single-point sampling at the seed lands in
  // the region's corner where colored.jpg tends to have shadow / line-bleed
  // and the picked color reads too dark to kids' eyes (round-1 dogfood
  // feedback: "補的顏色感覺都比較深"). Averaging valid points balances
  // corner shadow against center highlights into a more representative
  // mid-tone for the region.
  const points: Array<{ x: number; y: number }> = [
    { x: region.seedX, y: region.seedY },
    { x: Math.floor(region.centerX), y: Math.floor(region.centerY) },
  ]
  const { minX, minY, maxX, maxY } = region.bounds
  const dx = (maxX - minX) / 4
  const dy = (maxY - minY) / 4
  for (let j = 1; j <= 3; j++) {
    for (let i = 1; i <= 3; i++) {
      // Skip the centroid here — already in points[] above.
      if (i === 2 && j === 2) continue
      points.push({ x: Math.floor(minX + dx * i), y: Math.floor(minY + dy * j) })
    }
  }
  const samples: RGB[] = []
  for (const p of points) {
    const c = readPixel(coloredData, width, p.x, p.y)
    if (isValidSample(c)) samples.push(c)
  }
  if (samples.length === 0) return null
  const avg = {
    r: Math.round(samples.reduce((s, c) => s + c.r, 0) / samples.length),
    g: Math.round(samples.reduce((s, c) => s + c.g, 0) / samples.length),
    b: Math.round(samples.reduce((s, c) => s + c.b, 0) / samples.length),
  }
  // Apply brightness first (HSV V), then saturation (pulls channels away
  // from per-pixel luminance). Order matters: brightening after saturation
  // would let already-vivid channels clip the cap and lose the boost.
  return saturate(scaleBrightness(avg, BRIGHTNESS_MULTIPLIER), SATURATION_MULTIPLIER)
}

/**
 * Scale every channel by the same multiplier, capped so the brightest
 * channel never overflows 255. Hue and saturation stay constant (this is
 * the HSV value channel) so the result reads as the same color, just
 * brighter.
 */
function scaleBrightness(rgb: RGB, factor: number): RGB {
  const max = Math.max(rgb.r, rgb.g, rgb.b)
  if (max === 0) return rgb
  const safeFactor = Math.min(factor, 255 / max)
  return {
    r: Math.round(rgb.r * safeFactor),
    g: Math.round(rgb.g * safeFactor),
    b: Math.round(rgb.b * safeFactor),
  }
}

/**
 * Boost saturation by pulling each channel away from the pixel's luminance
 * by `factor`. Luminance stays roughly the same (channel distances scale
 * around the same midpoint), but dominant channels become more dominant
 * and weak channels recede. Hue is preserved — this is the standard
 * grayscale-anchored saturation operator.
 */
function saturate(rgb: RGB, factor: number): RGB {
  // Rec. 601 luminance coefficients — same weighting browsers use for
  // grayscale CSS filter, so the saturation lift reads as expected.
  const lum = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b
  return {
    r: clamp255(Math.round(lum + (rgb.r - lum) * factor)),
    g: clamp255(Math.round(lum + (rgb.g - lum) * factor)),
    b: clamp255(Math.round(lum + (rgb.b - lum) * factor)),
  }
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v
}

function readPixel(data: Uint8ClampedArray, width: number, x: number, y: number): RGB {
  const i = (y * width + x) * 4
  return { r: data[i]!, g: data[i + 1]!, b: data[i + 2]! }
}

function isValidSample(rgb: RGB): boolean {
  const tooWhite =
    rgb.r > SAMPLE_TOO_WHITE && rgb.g > SAMPLE_TOO_WHITE && rgb.b > SAMPLE_TOO_WHITE
  const tooBlack =
    rgb.r < SAMPLE_TOO_BLACK && rgb.g < SAMPLE_TOO_BLACK && rgb.b < SAMPLE_TOO_BLACK
  return !tooWhite && !tooBlack
}

export interface MagicFillResult {
  filled: Region[]
  skipped: Region[]
}

export interface RunMagicAutofillArgs {
  /** RGBA of current canvas — MUTATED in place if any region is filled. */
  canvasData: Uint8ClampedArray
  /** RGBA of reference colored image — read-only. */
  coloredData: Uint8ClampedArray
  width: number
  height: number
  /** Flood-fill threshold; reuse engine's configured value (default 50). */
  threshold: number
}

export interface RunMagicAutofillResult extends MagicFillResult {
  /** True if any region was filled (canvas mutated). False if nothing to do. */
  shouldCommit: boolean
}

/**
 * Pure orchestration of detect → sample → flood-fill. Mutates `canvasData`
 * in place only when at least one region was successfully sampled and filled.
 * Caller (ColoringEngine.magicAutofill) is responsible for the canvas
 * putImageData and history push when shouldCommit is true.
 */
export function runMagicAutofill(args: RunMagicAutofillArgs): RunMagicAutofillResult {
  const { canvasData, coloredData, width, height, threshold } = args
  const regions = detectUnfilledRegions({ data: canvasData, width, height })
  if (regions.length === 0) {
    return { filled: [], skipped: [], shouldCommit: false }
  }

  const filled: Region[] = []
  const skipped: Region[] = []

  for (const region of regions) {
    const color = sampleRegionColor({ coloredData, width, region })
    if (!color) {
      skipped.push(region)
      continue
    }
    // Start the flood from the seed pixel (guaranteed inside the region).
    // The centroid can land on a line for concave shapes, in which case
    // floodFill bails with `on-line` and the region stays white.
    const result = floodFill({
      data: canvasData,
      width,
      height,
      startX: region.seedX,
      startY: region.seedY,
      replacement: color,
      threshold,
    })
    if (result.status === 'filled') {
      filled.push(region)
    } else {
      // floodFill couldn't paint (on-line, same-color, no-image). Record as
      // skipped so the caller's user-facing toast is honest about coverage.
      skipped.push(region)
    }
  }

  return { filled, skipped, shouldCommit: filled.length > 0 }
}
